import type { EmployeeDto } from '@glory/contracts';
import { formatDateTime, formatHours, formatMoney, t } from '@glory/i18n';
import {
  Avatar,
  Badge,
  Button,
  Callout,
  cn,
  dataColumns,
  DataTable,
  Dialog,
  EmptyState,
  HybridPanel,
  notify,
  PageHeader,
  SettingsSection,
  SwitchField,
  Tabs,
  TabsList,
  TabsTrigger,
} from '@glory/ui';
import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { UserPlus, Users } from 'lucide-react';
import { AnimatePresence } from 'motion/react';
import { useMemo, useState } from 'react';

import { ApplicationsTab } from '../../../features/team/applications-tab.tsx';
import { billableMinutes, EmployeeCard, RoleBadges } from '../../../features/team/employee-card.tsx';
import { InviteDialog } from '../../../features/team/invite-dialog.tsx';
import { VacanciesTab } from '../../../features/team/vacancies-tab.tsx';
import { client, orpc } from '../../../lib/orpc.ts';
import { requirePermissionRoute, usePermissions } from '../../../lib/permissions.ts';
import { meQuery } from '../../../lib/session.ts';

type TeamTab = 'team' | 'vacancies' | 'applications';
const TEAM_TABS: readonly TeamTab[] = ['team', 'vacancies', 'applications'];

/**
 * Открытая карточка живёт в адресе: ссылку на сотрудника можно переслать, ⌘K открывает её же. Вкладки «Вакансии» и
 * «Отклики» (подпроект 12) — у `employees.hiring`; отклик из уведомления открывается по `application`.
 */
interface TeamSearch {
  readonly employee?: string | undefined;
  readonly tab?: TeamTab | undefined;
  readonly application?: string | undefined;
}

export const Route = createFileRoute('/_app/team/')({
  validateSearch: (search: Record<string, unknown>): TeamSearch => ({
    employee: typeof search.employee === 'string' ? search.employee : undefined,
    tab: TEAM_TABS.find((tab) => tab === search.tab),
    application: typeof search.application === 'string' ? search.application : undefined,
  }),
  beforeLoad: requirePermissionRoute('employees.read'),
  component: TeamPage,
});

const col = dataColumns<EmployeeDto>();

function teamColumns({ selfId, withRates }: { selfId: string; withRates: boolean }) {
  return col.columns([
    col.display({
      id: 'person',
      header: t('team.columnPerson'),
      meta: { width: 'minmax(0,1.6fr)', noClip: true },
      cell: (c) => {
        const employee = c.row.original;
        return (
          <span className="flex min-w-0 items-center gap-2.5">
            <Avatar name={employee.name} src={employee.image} />
            <span className="flex min-w-0 flex-col">
              <span className="flex min-w-0 items-center gap-1.5">
                <span className={cn('min-w-0 truncate font-semibold', !employee.active && 'text-muted')}>
                  {employee.name}
                </span>
                {employee.id === selfId ? (
                  <Badge tone="accent" className="shrink-0">
                    {t('team.you')}
                  </Badge>
                ) : null}
                {employee.active ? null : (
                  <Badge tone="danger" className="shrink-0">
                    {t('team.inactive')}
                  </Badge>
                )}
              </span>
              <span className="truncate text-caption text-muted">{employee.position ?? employee.email}</span>
            </span>
          </span>
        );
      },
    }),
    col.display({
      id: 'specializations',
      header: t('team.columnSpecializations'),
      meta: { width: 'minmax(0,1.4fr)', hideOnMobile: true },
      cell: (c) => {
        const own = c.row.original.specializations.filter((s) => !s.archived);
        if (own.length === 0) return <span className="text-muted">—</span>;
        return (
          <span className="flex min-w-0 flex-wrap gap-1">
            {own.map((s) => (
              <Badge key={s.id} className="shrink-0">
                {s.name}
              </Badge>
            ))}
          </span>
        );
      },
    }),
    col.display({
      id: 'roles',
      header: t('team.columnRoles'),
      meta: { width: 'minmax(0,1.2fr)', hideOnMobile: true },
      cell: (c) => (
        <span className="flex min-w-0 flex-wrap gap-1">
          <RoleBadges roles={c.row.original.roles} />
        </span>
      ),
    }),
    col.display({
      id: 'capacity',
      header: t('team.columnCapacity'),
      meta: { width: '9.5rem', align: 'right', hideOnMobile: true },
      cell: (c) =>
        t('team.capacityValue', {
          billable: formatHours(billableMinutes(c.row.original)),
          total: formatHours(c.row.original.weeklyCapacity),
        }),
    }),
    ...(withRates
      ? [
          col.display({
            id: 'rate',
            header: t('team.columnRate'),
            meta: { width: '6rem', align: 'right', hideOnMobile: true },
            cell: (c) => {
              const rate = c.row.original.currentRate ?? null;
              return rate === null ? (
                <span className="text-muted">—</span>
              ) : (
                <span className="font-semibold">{formatMoney(rate, { perHour: true })}</span>
              );
            },
          }),
        ]
      : []),
  ]);
}

function TeamPage() {
  const search = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data: me } = useSuspenseQuery(meQuery);
  const { can } = usePermissions();
  const hiring = can('employees.hiring');
  const tab: TeamTab = hiring ? (search.tab ?? 'team') : 'team';
  const [showInactive, setShowInactive] = useState(false);
  const [inviting, setInviting] = useState(false);
  const team = useQuery(orpc.org.team.list.queryOptions());
  const canInvite = can('users.invite');
  const withRates = can('rates.read.all');
  const columns = useMemo(() => teamColumns({ selfId: me.id, withRates }), [me.id, withRates]);

  const all = team.data?.items ?? [];
  const hasInactive = all.some((e) => !e.active);
  // Отключённого из ⌘K или по ссылке показываем, даже если отключённые скрыты.
  const items = all.filter((e) => e.active || showInactive || e.id === search.employee);
  const openIndex = items.findIndex((e) => e.id === search.employee);
  const open = openIndex >= 0 ? items[openIndex] : undefined;

  /** Открыть карточку — новая запись истории (назад закроет её); листать и закрыть — без записи. */
  const show = (id: string | undefined, replace: boolean) => {
    void navigate({ search: id ? { employee: id } : {}, replace });
  };

  return (
    <div className="relative flex min-h-full w-full flex-col gap-4 px-4 py-5 md:px-6">
      <PageHeader
        title={t('team.title')}
        actions={
          canInvite ? (
            <Button
              variant="primary"
              onClick={() => {
                setInviting(true);
              }}
            >
              <UserPlus /> {t('team.invite')}
            </Button>
          ) : null
        }
      />
      <p className="-mt-2 max-w-[70ch] text-muted">{t('team.lead')}</p>
      {hiring ? (
        <Tabs
          value={tab}
          onValueChange={(next) => {
            void navigate({ search: next === 'team' ? {} : { tab: next as TeamTab } });
          }}
        >
          <TabsList>
            <TabsTrigger value="team">{t('team.hiring.tabs.team')}</TabsTrigger>
            <TabsTrigger value="vacancies">{t('team.hiring.tabs.vacancies')}</TabsTrigger>
            <TabsTrigger value="applications">{t('team.hiring.tabs.applications')}</TabsTrigger>
          </TabsList>
        </Tabs>
      ) : null}
      {tab === 'vacancies' ? <VacanciesTab /> : null}
      {tab === 'applications' ? (
        <ApplicationsTab
          selectedId={search.application}
          onSelect={(application) => {
            void navigate({ search: { tab: 'applications', application }, replace: true });
          }}
        />
      ) : null}
      {tab !== 'team' ? null : (
        <>
          {hasInactive ? (
            <SwitchField
              inline
              label={t('team.showInactive')}
              checked={showInactive}
              onCheckedChange={setShowInactive}
            />
          ) : null}

          {team.isError ? (
            <Callout>{team.error.message}</Callout>
          ) : (
            <DataTable
              aria-label={t('team.title')}
              data={items}
              columns={columns}
              getRowId={(employee) => employee.id}
              loading={team.isPending}
              activeRowId={open?.id ?? null}
              onRowActivate={(employee) => {
                show(employee.id, open !== undefined);
              }}
              empty={<EmptyState icon={<Users />} title={t('team.empty')} />}
            />
          )}

          {canInvite ? <PendingInvitations /> : null}
        </>
      )}

      <AnimatePresence initial={false}>
        {open && team.data ? (
          <EmployeeCard key="panel" employee={open} today={team.data.today}>
            {(card) => (
              <HybridPanel
                title={card.title}
                meta={card.meta}
                tabs={card.tabs}
                actions={card.actions}
                onClose={() => {
                  show(undefined, true);
                }}
                onPrevious={() => {
                  const previous = items[openIndex - 1];
                  if (previous) show(previous.id, true);
                }}
                onNext={() => {
                  const next = items[openIndex + 1];
                  if (next) show(next.id, true);
                }}
                onExpand={() => {
                  void navigate({ to: '/team/$id', params: { id: open.id } });
                }}
              >
                {card.body}
              </HybridPanel>
            )}
          </EmployeeCard>
        ) : null}
      </AnimatePresence>

      <Dialog open={inviting} onOpenChange={setInviting}>
        {inviting ? (
          <InviteDialog
            onDone={() => {
              setInviting(false);
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

/** Приглашения, которые ещё не приняты: кому, с какими ролями, до какого срока. */
function PendingInvitations() {
  const queryClient = useQueryClient();
  const invitations = useQuery(orpc.identity.invitations.list.queryOptions({ input: {} }));
  const revoke = useMutation({
    mutationFn: (invitation: { id: string; email: string }) =>
      client.identity.invitations.revoke({ id: invitation.id }),
    onSuccess: async (_, invitation) => {
      notify(t('team.revoked', { email: invitation.email }));
      await queryClient.invalidateQueries({ queryKey: orpc.identity.invitations.key() });
    },
  });
  const pending = (invitations.data ?? []).filter((i) => i.status === 'pending');
  if (pending.length === 0) return null;

  return (
    <SettingsSection title={t('team.invitationsTitle')} lead={t('team.invitationsLead')}>
      <ul className="-my-2 flex flex-col divide-y divide-border-soft">
        {pending.map((invitation) => (
          <li key={invitation.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 py-2">
            <div className="flex min-w-0 flex-1 basis-full flex-col sm:basis-0">
              <span className="truncate font-semibold">{invitation.name ?? invitation.email}</span>
              <span className="truncate text-caption text-muted">
                {[invitation.name ? invitation.email : null, invitation.position].filter(Boolean).join(', ')}
              </span>
            </div>
            <span className="flex flex-wrap gap-1">
              <RoleBadges roles={invitation.roles} />
            </span>
            <span className="text-caption text-muted tabular-nums">
              {t('team.invitationExpires', { date: formatDateTime(invitation.expiresAt) })}
            </span>
            <Button
              size="sm"
              variant="ghost"
              loading={revoke.isPending && revoke.variables.id === invitation.id}
              onClick={() => {
                revoke.mutate(invitation);
              }}
            >
              {t('team.revoke')}
            </Button>
            {revoke.isError && revoke.variables.id === invitation.id ? (
              <p className="basis-full text-caption font-semibold text-danger">{revoke.error.message}</p>
            ) : null}
          </li>
        ))}
      </ul>
    </SettingsSection>
  );
}
